import { memo, useState } from 'react'
import {
  VStack,
  HStack,
  Box,
  Text,
  Heading,
  Card,
  CardBody,
  Button,
  Icon,
  Radio,
  RadioGroup,
  Stack,
  Checkbox,
  CheckboxGroup,
  useColorModeValue,
  useToast,
  Badge,
  Divider,
} from '@chakra-ui/react'
import {
  LuFileText,
  LuFileDown,
  LuPresentation,
  LuDownload,
  LuCircleCheckBig,
} from 'react-icons/lu'
import { useProjectSources } from '../../../../api/api_hooks/project-sources'
import { exportProject } from '../../utils/exportGenerator'

interface ExportViewProps {
  projectId: string
  projectTitle: string
}

export const ExportView = memo(function ExportView({
  projectId,
  projectTitle,
}: ExportViewProps) {
  const toast = useToast()
  const [exportFormat, setExportFormat] = useState<'markdown' | 'word' | 'slides'>('markdown')
  const [includeSections, setIncludeSections] = useState<string[]>([
    'sources',
    'entities',
    'protocols',
    'methods',
    'results',
  ])

  const { data: sourcesData } = useProjectSources(projectId)
  const sources = sourcesData?.sources || []

  const cardBg = useColorModeValue('white', 'gray.700')
  const borderColor = useColorModeValue('gray.200', 'gray.600')

  const [isExporting, setIsExporting] = useState(false)

  const handleExport = async () => {
    if (exportFormat === 'slides') {
      toast({
        title: 'Format non disponible',
        description: 'L\'export PowerPoint sera bientôt disponible',
        status: 'warning',
        duration: 3000,
      })
      return
    }

    setIsExporting(true)

    toast({
      title: 'Export en cours',
      description: `Génération du fichier ${exportFormat.toUpperCase()}...`,
      status: 'info',
      duration: 3000,
    })

    try {
      await exportProject({
        projectTitle,
        sources,
        includeSections,
        format: exportFormat as 'markdown' | 'word',
      })

      toast({
        title: 'Export réussi',
        description: `Le fichier a été téléchargé avec succès`,
        status: 'success',
        duration: 5000,
      })
    } catch (error) {
      console.error('Export error:', error)
      toast({
        title: "Erreur d'export",
        description: error instanceof Error ? error.message : "Une erreur s'est produite",
        status: 'error',
        duration: 5000,
      })
    } finally {
      setIsExporting(false)
    }
  }

  const formatOptions = [
    {
      value: 'markdown',
      label: 'Markdown',
      icon: LuFileText,
      description: 'Fichier .md compatible avec toutes les plateformes',
      badge: 'Recommandé',
    },
    {
      value: 'word',
      label: 'Microsoft Word',
      icon: LuFileDown,
      description: 'Document .docx pour édition',
      badge: null,
    },
    {
      value: 'slides',
      label: 'PowerPoint',
      icon: LuPresentation,
      description: 'Présentation .pptx avec synthèse visuelle',
      badge: 'Bientôt',
    },
  ]

  const sectionOptions = [
    { value: 'sources', label: 'Liste des sources', count: sources.length },
    {
      value: 'entities',
      label: 'Entités biologiques',
      count: sources.filter((s) => s.extractedEntities?.entities).length,
    },
    {
      value: 'protocols',
      label: 'Protocoles expérimentaux',
      count: sources.filter((s) => s.extractedEntities?.protocols).length,
    },
    {
      value: 'methods',
      label: 'Méthodes',
      count: sources.filter((s) => s.extractedEntities?.methods).length,
    },
    {
      value: 'results',
      label: 'Résultats & Statistiques',
      count: sources.filter((s) => s.extractedEntities?.results).length,
    },
  ]

  return (
    <VStack spacing={6} align="stretch" h="full" overflow="auto" px={2}>
      {/* Header */}
      <Box
        bgGradient="linear(135deg, purple.50 0%, purple.100 100%)"
        p={6}
        borderRadius="xl"
        border="1px solid"
        borderColor="purple.200"
      >
        <Heading size="md" mb={2} color="purple.800">
          Exporter le projet
        </Heading>
        <Text fontSize="sm" color="purple.700">
          Générez un document synthétique de votre recherche
        </Text>
      </Box>

      {/* Format Selection */}
      <Card bg={cardBg} borderColor={borderColor} borderWidth="1px" shadow="md" borderRadius="xl">
        <CardBody>
          <Heading size="sm" mb={4} color="gray.700" _dark={{ color: 'gray.200' }}>
            Format d'export
          </Heading>
          <RadioGroup value={exportFormat} onChange={(val: any) => setExportFormat(val)}>
            <Stack spacing={4}>
              {formatOptions.map((option) => (
                <Box
                  key={option.value}
                  p={4}
                  borderWidth="2px"
                  borderColor={
                    exportFormat === option.value ? 'purple.500' : borderColor
                  }
                  borderRadius="lg"
                  cursor={option.badge === 'Bientôt' ? 'not-allowed' : 'pointer'}
                  opacity={option.badge === 'Bientôt' ? 0.5 : 1}
                  onClick={() => {
                    if (option.badge !== 'Bientôt') {
                      setExportFormat(option.value as any)
                    }
                  }}
                  bg={exportFormat === option.value ? 'purple.50' : 'transparent'}
                  _dark={{
                    bg: exportFormat === option.value ? 'purple.900' : 'transparent',
                  }}
                  transition="all 0.2s"
                  _hover={{
                    borderColor: option.badge === 'Bientôt' ? borderColor : 'purple.400',
                    shadow: option.badge === 'Bientôt' ? 'none' : 'md',
                  }}
                >
                  <Radio
                    value={option.value}
                    isDisabled={option.badge === 'Bientôt'}
                  >
                    <HStack spacing={3}>
                      <Icon as={option.icon} boxSize={5} />
                      <Box flex={1}>
                        <HStack>
                          <Text fontWeight="medium">{option.label}</Text>
                          {option.badge && (
                            <Badge
                              colorScheme={
                                option.badge === 'Recommandé' ? 'green' : 'gray'
                              }
                            >
                              {option.badge}
                            </Badge>
                          )}
                        </HStack>
                        <Text fontSize="sm" color="gray.600">
                          {option.description}
                        </Text>
                      </Box>
                    </HStack>
                  </Radio>
                </Box>
              ))}
            </Stack>
          </RadioGroup>
        </CardBody>
      </Card>

      {/* Sections to Include */}
      <Card bg={cardBg} borderColor={borderColor} borderWidth="1px" shadow="md" borderRadius="xl">
        <CardBody>
          <Heading size="sm" mb={4} color="gray.700" _dark={{ color: 'gray.200' }}>
            Sections à inclure
          </Heading>
          <CheckboxGroup value={includeSections} onChange={(val) => setIncludeSections(val as string[])}>
            <Stack spacing={3}>
              {sectionOptions.map((option) => (
                <HStack key={option.value} justify="space-between">
                  <Checkbox value={option.value} colorScheme="purple">
                    <Text fontSize="sm">{option.label}</Text>
                  </Checkbox>
                  <Badge colorScheme={option.count > 0 ? 'purple' : 'gray'} variant="subtle">
                    {option.count} élément{option.count > 1 ? 's' : ''}
                  </Badge>
                </HStack>
              ))}
            </Stack>
          </CheckboxGroup>
        </CardBody>
      </Card>

      {/* Preview Info */}
      <Card
        bg="purple.50"
        borderColor="purple.200"
        borderWidth="2px"
        shadow="lg"
        borderRadius="xl"
        _dark={{ bg: 'purple.900', borderColor: 'purple.700' }}
      >
        <CardBody>
          <Heading size="sm" mb={4} color="purple.800" _dark={{ color: 'purple.200' }}>
            Aperçu du contenu
          </Heading>
          <VStack align="stretch" spacing={3} fontSize="sm">
            <HStack>
              <Icon as={LuCircleCheckBig} color="purple.600" boxSize={5} />
              <Text fontWeight="medium" color="purple.900" _dark={{ color: 'purple.100' }}>
                Titre: {projectTitle}
              </Text>
            </HStack>
            <HStack>
              <Icon as={LuCircleCheckBig} color="purple.600" boxSize={5} />
              <Text fontWeight="medium" color="purple.900" _dark={{ color: 'purple.100' }}>
                {sources.length} source{sources.length > 1 ? 's' : ''} référencée{sources.length > 1 ? 's' : ''}
              </Text>
            </HStack>
            <HStack>
              <Icon as={LuCircleCheckBig} color="purple.600" boxSize={5} />
              <Text fontWeight="medium" color="purple.900" _dark={{ color: 'purple.100' }}>
                {includeSections.length} section{includeSections.length > 1 ? 's' : ''} incluse{includeSections.length > 1 ? 's' : ''}
              </Text>
            </HStack>
            <Divider my={2} borderColor="purple.300" />
            <Box bg="purple.100" p={3} borderRadius="md" _dark={{ bg: 'purple.800' }}>
              <Text fontSize="xs" color="purple.800" _dark={{ color: 'purple.200' }}>
                💡 L'export contiendra tous les éléments extraits automatiquement par l'IA
                (protocoles, entités, méthodes, résultats) des sources sélectionnées.
              </Text>
            </Box>
          </VStack>
        </CardBody>
      </Card>

      {/* Export Button */}
      <Box>
        <Button
          leftIcon={<Icon as={LuDownload} boxSize={5} />}
          colorScheme="purple"
          size="lg"
          w="full"
          onClick={handleExport}
          isLoading={isExporting}
          loadingText="Génération en cours..."
          isDisabled={
            includeSections.length === 0 ||
            sources.length === 0 ||
            exportFormat === 'slides'
          }
          py={7}
          fontSize="md"
          fontWeight="bold"
          borderRadius="xl"
          boxShadow="lg"
          _hover={{
            transform: 'translateY(-2px)',
            boxShadow: '2xl',
          }}
          _active={{
            transform: 'translateY(0)',
          }}
          transition="all 0.2s"
          bgGradient="linear(to-r, purple.500, purple.600)"
          _disabled={{
            bgGradient: 'none',
            opacity: 0.6,
            cursor: 'not-allowed',
            transform: 'none',
            boxShadow: 'none',
          }}
        >
          Générer et télécharger
        </Button>
        {sources.length === 0 && (
          <Text fontSize="xs" color="red.500" textAlign="center" mt={2}>
            Aucune source dans le projet
          </Text>
        )}
        {exportFormat === 'slides' && (
          <Text fontSize="xs" color="orange.500" textAlign="center" mt={2}>
            L'export PowerPoint sera bientôt disponible
          </Text>
        )}
      </Box>
    </VStack>
  )
})
